import React, { useState } from 'react';
import Editor from 'react-simple-wysiwyg';
import { formatDate } from '../../utils/formatters';
import { fetchPolicyById, updatePolicy } from '../../api/adminClient';

const PolicyManagement = ({ policies = [], loading }) => {
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [actionLoading, setActionLoading] = useState(false);

  const handleViewPolicy = async (policyId) => {
    console.log('Viewing policy with ID:', policyId);
    setActionLoading(true);
    try {
      const response = await fetchPolicyById(policyId);
      if (response.data?.success) {
        setSelectedPolicy(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch policy details:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditPolicy = (policy) => {
    setSelectedPolicy(policy);

    // Convert ISO date to YYYY-MM-DD format for date input
    const formatDateForInput = (isoDate) => {
      if (!isoDate) return '';
      const date = new Date(isoDate);
      return date.toISOString().split('T')[0];
    };

    setEditForm({
      title: policy.title,
      content: policy.content,
      effectiveDate: formatDateForInput(policy.effectiveDate)
    });
    setIsEditing(true);
  };

  const handleSavePolicy = async () => {
    if (!selectedPolicy?.id) return;

    setActionLoading(true);
    try {
      await updatePolicy(selectedPolicy.id, editForm);
      setIsEditing(false);
      setSelectedPolicy(null);
      // Refresh the page to show updated data
      window.location.reload();
    } catch (error) {
      console.error('Failed to update policy:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
    setSelectedPolicy(null);
  };

  if (selectedPolicy && !isEditing) {
    return (
      <div className="policy-detail-view">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <button
            type="button"
            className="btn btn-outline-secondary rounded-pill"
            onClick={() => setSelectedPolicy(null)}
          >
            ← Back to Policies
          </button>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-primary rounded-pill"
              onClick={() => handleEditPolicy(selectedPolicy)}
            >
              Edit Policy
            </button>
          </div>
        </div>

        <div className="card-soft">
          <div className="p-4">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h1 className="display-6 fw-semibold mb-2">{selectedPolicy.title}</h1>
                <small className="text-muted">Effective {formatDate(selectedPolicy.effectiveDate)}</small>
              </div>
              <span className="badge rounded-pill shadow-sm bg-secondary-subtle text-secondary">
                ID {selectedPolicy.id}
              </span>
            </div>
            <div className="policy-content">
              <h5 className="fw-semibold mb-3">Summary</h5>
              <p className="text-muted mb-4">{selectedPolicy.summary}</p>

              <h5 className="fw-semibold mb-3">Full Content</h5>
              <div
                className="policy-text bg-light rounded p-4"
                dangerouslySetInnerHTML={{ __html: selectedPolicy.content }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEditing && selectedPolicy) {
    return (
      <div className="policy-edit-view">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <button
            type="button"
            className="btn btn-outline-secondary rounded-pill"
            onClick={handleCancelEdit}
          >
            ← Cancel
          </button>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-success rounded-pill"
              onClick={handleSavePolicy}
              disabled={actionLoading}
            >
              {actionLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        <div className="card-soft">
          <div className="p-4">
            <form>
              <div className="mb-4">
                <label htmlFor="policyTitle" className="form-label fw-semibold">Policy Title</label>
                <input
                  type="text"
                  className="form-control"
                  id="policyTitle"
                  value={editForm.title || ''}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="policyContent" className="form-label fw-semibold">Full Content</label>
                <Editor
                  value={editForm.content || ''}
                  onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                  containerProps={{ style: { minHeight: '300px' } }}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="effectiveDate" className="form-label fw-semibold">Effective Date</label>
                <input
                  type="date"
                  className="form-control"
                  id="effectiveDate"
                  value={editForm.effectiveDate || ''}
                  onChange={(e) => setEditForm({ ...editForm, effectiveDate: e.target.value })}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="policies-view">
      <div className="d-flex flex-column flex-lg-row gap-3 mb-4">
        <div>
          <h1 className="display-6 fw-semibold mb-1">Policy Management</h1>
          <p className="text-muted mb-0">
            Maintain legal documentation, track effective dates, and publish updates.
          </p>
        </div>
      </div>

      <div className="row g-4">
        {policies.length > 0 ? (
          policies.map((policy) => (
            <div key={policy.id} className="col-12 col-lg-6">
              <div className="card-soft h-100">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h5 className="fw-semibold mb-1">{policy.title}</h5>
                    <small className="text-muted">Effective {formatDate(policy.effectiveDate)}</small>
                  </div>
                </div>
                <div className="d-flex gap-2 mt-3">
                  <button
                    type="button"
                    className="btn btn-primary rounded-pill shadow-sm flex-grow-1"
                    disabled={loading || actionLoading}
                    onClick={() => handleViewPolicy(policy.id)}
                  >
                    View Details
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary rounded-pill shadow-sm flex-grow-1"
                    disabled={loading || actionLoading}
                    onClick={() => handleEditPolicy(policy)}
                  >
                    Edit Policy
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-12">
            <div className="card-soft text-center py-5">
              <h5 className="text-muted mb-3">No policies found</h5>
              <p className="text-muted mb-0">No policies are currently available.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicyManagement;
